import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Parser from 'html-react-parser';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { hideConfirmationModal } from '../actions/App';

import ProcessingProgress from './ProcessingProgress';
import ProcessingSpinner from './ProcessingSpinner';

import Console from '../helpers/Console';

class ConfirmationModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      skip: false,
      processing: false,
      error: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.getText = this.getText.bind(this);
  }

  getProgress() {
    const {
      progress,
      backendActions,
      confirmationModalParams,
    } = this.props;
    const { progressGroupUuid } = confirmationModalParams;

    if (progressGroupUuid) {
      return backendActions[`progress_${progressGroupUuid}`] || { total: 0, done: 0 };
    }

    return progress;
  }

  handleClose() {
    const { closeConfirmationModal } = this.props;
    this.setState({ error: false }, () => {
      closeConfirmationModal();
    });
  }

  handleConfirm() {
    const { confirmationModalParams } = this.props;
    this.setState({
      processing: true,
      error: false,
    });
    confirmationModalParams.onConfirm().then(() => {
      this.setState({ processing: false });
      this.handleClose();
    }).catch((err) => {
      Console.log(err);
      this.setState({
        processing: false,
        error: true,
      });
    });
  }

  handleCancel() {
    const { confirmationModalParams } = this.props;
    confirmationModalParams.onCancel().then(() => {
      this.handleClose();
    });
  }

  getText() {
    const {
      intl,
      confirmationModalParams,
    } = this.props;
    const { processing } = this.state;

    if (processing && confirmationModalParams.processingText) {
      return confirmationModalParams.processingText;
    }
    return confirmationModalParams.confirmText ? confirmationModalParams.confirmText : intl.formatMessage({ defaultMessage: 'OK' });
  }

  render() {
    const {
      intl,
      confirmationModal,
      confirmationModalParams,
      skipKey,
    } = this.props;
    const {
      skip,
      processing,
      error,
    } = this.state;
    const { errorMessage } = confirmationModalParams;

    if (skip === 999) {
      return null;
    }

    const progress = this.getProgress();

    return (
      <Modal
        className="modal-confirmation"
        isOpen={confirmationModal}
        toggle={this.handleCancel}
      >
        <ModalHeader className="modal-header-center">
          {confirmationModalParams.title ? confirmationModalParams.title : intl.formatMessage({ defaultMessage: 'Are you sure?' })}
        </ModalHeader>
        <ModalBody>
          {confirmationModalParams.warning && (
            <p className="warning">
              <i className="far fa-triangle-exclamation fa-2x" />
              {typeof confirmationModalParams.warning === 'string' ?
                Parser(confirmationModalParams.warning) : confirmationModalParams.warning}
            </p>
          )}
          <p>
            {typeof confirmationModalParams.text === 'string' ?
              Parser(confirmationModalParams.text) : confirmationModalParams.text}
          </p>
          {skipKey && (
            <Form className="confirm">
              <FormGroup check>
                <Label for="skipThis" check>
                  <Input type="checkbox" id="skipThis" />
                  {' '}
                  {intl.formatMessage({ defaultMessage: 'do not show this confirmation again' })}
                </Label>
              </FormGroup>
            </Form>
          )}
          {(error || errorMessage) && (
            <p className="error">
              {errorMessage ? (
                `${errorMessage}`
              ) : (
                intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again or contact us.' })
              )}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            {!processing && (
              <Button
                color="secondary"
                disabled={processing}
                onClick={this.handleCancel}
              >
                {confirmationModalParams.cancelText ? confirmationModalParams.cancelText : intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
            )}
            <Button
              className="confirmation-button"
              color="primary"
              disabled={processing || !!errorMessage}
              onClick={this.handleConfirm}
            >
              <ProcessingSpinner show={processing} />
              {this.getText()}
              <ProcessingProgress
                show={processing}
                progress={progress}
              />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  settings: state.App.settings,
  progress: state.App.progress,
  confirmationModal: state.App.confirmationModal,
  confirmationModalParams: state.App.confirmationModalParams,
  backendActions: state.App.backendActions,
});

const mapDispatchToProps = dispatch => ({
  closeConfirmationModal: () => {
    dispatch(hideConfirmationModal());
  },
});

ConfirmationModal.propTypes = {
  intl: PropTypes.object.isRequired,
  progress: PropTypes.object.isRequired,
  confirmationModal: PropTypes.bool.isRequired,
  confirmationModalParams: PropTypes.object.isRequired,
  backendActions: PropTypes.object.isRequired,
  closeConfirmationModal: PropTypes.func.isRequired,
  skipKey: PropTypes.string,
};

ConfirmationModal.defaultProps = {
  skipKey: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ConfirmationModal));
